import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
import { DollarSignIcon, CreditCardIcon, WalletIcon, FileTextIcon, DownloadIcon, ExternalLinkIcon, PlusIcon, ChevronDownIcon, XIcon, AlertCircleIcon, CheckIcon, CalendarIcon } from 'lucide-react';
import { useTheme } from '../providers/ThemeProvider';
import { showToast } from '../ToastContainer';
interface PaymentMethod {
  id: string;
  type: 'card';
  name: string;
  cardNumber: string;
  last4: string;
  expiryMonth: string;
  expiryYear: string;
  cvc?: string;
  zip?: string;
  isDefault: boolean;
  brand: string;
}
export const BillingPayoutsPage = () => {
  const {
    userType
  } = useAuth();
  const {
    theme
  } = useTheme();
  const darkMode = theme === 'dark';
  // Payment methods state
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([{
    id: '1',
    type: 'card',
    name: 'John Smith',
    cardNumber: '4242424242424242',
    last4: '4242',
    expiryMonth: '12',
    expiryYear: '2025',
    cvc: '123',
    zip: '10001',
    isDefault: true,
    brand: 'visa'
  }]);
  // Bank account state
  const [bankAccount, setBankAccount] = useState({
    bankName: 'Chase Bank',
    accountNumber: '12345678901234',
    last4: '4567',
    accountHolderName: 'John Smith',
    routingNumber: '123456789',
    accountType: 'checking'
  });
  // Subscription state
  const [subscription, setSubscription] = useState({
    plan: 'Pro',
    interval: 'Monthly',
    price: 19.99,
    nextRenewal: 'Nov 15, 2025',
    isActive: true
  });
  // Modal states
  const [isManageSubOpen, setIsManageSubOpen] = useState(false);
  const [isConfirmCancelOpen, setIsConfirmCancelOpen] = useState(false);
  const [isCardModalOpen, setIsCardModalOpen] = useState(false);
  const [cardModalMode, setCardModalMode] = useState<'add' | 'edit'>('add');
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [isPayoutModalOpen, setIsPayoutModalOpen] = useState(false);
  const [isConfirmPayoutOpen, setIsConfirmPayoutOpen] = useState(false);
  const [isRemoveCardModalOpen, setIsRemoveCardModalOpen] = useState(false);
  // Loading states
  const [isProcessing, setIsProcessing] = useState(false);
  // Form states
  const [formCard, setFormCard] = useState<Omit<PaymentMethod, 'id' | 'isDefault' | 'type' | 'last4' | 'brand'>>({
    name: '',
    cardNumber: '',
    expiryMonth: '',
    expiryYear: '',
    cvc: '',
    zip: ''
  });
  const [formPayout, setFormPayout] = useState({
    accountHolderName: '',
    routingNumber: '',
    accountNumber: '',
    accountType: 'checking'
  });
  // Form errors
  const [cardErrors, setCardErrors] = useState({
    name: '',
    cardNumber: '',
    expiry: '',
    cvc: '',
    zip: ''
  });
  const [payoutErrors, setPayoutErrors] = useState({
    accountHolderName: '',
    routingNumber: '',
    accountNumber: '',
    accountType: ''
  });
  // Handle subscription modal
  const handleOpenManageSub = () => {
    setIsManageSubOpen(true);
  };
  const handleOpenConfirmCancel = () => {
    setIsManageSubOpen(false);
    setIsConfirmCancelOpen(true);
  };
  const handleConfirmCancel = () => {
    setIsProcessing(true);
    // Simulate API call
    setTimeout(() => {
      setSubscription({
        ...subscription,
        isActive: false
      });
      setIsConfirmCancelOpen(false);
      setIsProcessing(false);
      showToast.success(`Subscription canceled. You'll keep access until ${subscription.nextRenewal}.`);
    }, 600);
  };
  // Handle payment method modal
  const handleOpenCardModal = (mode: 'add' | 'edit', cardId?: string) => {
    setCardModalMode(mode);
    if (mode === 'edit' && cardId) {
      setSelectedCardId(cardId);
      const card = paymentMethods.find(method => method.id === cardId);
      if (card) {
        setFormCard({
          name: card.name,
          cardNumber: card.cardNumber,
          expiryMonth: card.expiryMonth,
          expiryYear: card.expiryYear,
          cvc: card.cvc || '',
          zip: card.zip || ''
        });
      }
    } else {
      // Reset form for add mode
      setSelectedCardId(null);
      setFormCard({
        name: '',
        cardNumber: '',
        expiryMonth: '',
        expiryYear: '',
        cvc: '',
        zip: ''
      });
    }
    setIsCardModalOpen(true);
  };
  const handleOpenRemoveCardModal = (cardId: string) => {
    setSelectedCardId(cardId);
    setIsRemoveCardModalOpen(true);
  };
  const handleRemoveCard = () => {
    setIsProcessing(true);
    // Simulate API call
    setTimeout(() => {
      if (selectedCardId) {
        setPaymentMethods(paymentMethods.filter(method => method.id !== selectedCardId));
      }
      setIsRemoveCardModalOpen(false);
      setSelectedCardId(null);
      setIsProcessing(false);
      showToast.success('Payment method removed.');
    }, 500);
  };
  // Validate card form
  const validateCardForm = (): boolean => {
    const errors = {
      name: '',
      cardNumber: '',
      expiry: '',
      cvc: '',
      zip: ''
    };
    let isValid = true;
    // Name validation
    if (!formCard.name.trim()) {
      errors.name = 'Name is required';
      isValid = false;
    }
    // Card number validation (simple Luhn check would go here)
    const cardNumber = formCard.cardNumber.replace(/\s/g, '');
    if (!cardNumber || cardNumber.length !== 16 || !/^\d+$/.test(cardNumber)) {
      errors.cardNumber = 'Enter a valid 16-digit card number';
      isValid = false;
    }
    // Expiry validation
    const currentYear = new Date().getFullYear() % 100;
    const currentMonth = new Date().getMonth() + 1;
    if (!formCard.expiryMonth || !formCard.expiryYear) {
      errors.expiry = 'Expiry date is required';
      isValid = false;
    } else if (parseInt(formCard.expiryMonth) < 1 || parseInt(formCard.expiryMonth) > 12) {
      errors.expiry = 'Enter a valid month (01-12)';
      isValid = false;
    } else if (parseInt(formCard.expiryYear) < currentYear || parseInt(formCard.expiryYear) === currentYear && parseInt(formCard.expiryMonth) < currentMonth) {
      errors.expiry = 'Expiry date is in the past';
      isValid = false;
    }
    // CVC validation
    if (!formCard.cvc || !/^\d{3,4}$/.test(formCard.cvc)) {
      errors.cvc = 'Enter a valid 3 or 4 digit CVC';
      isValid = false;
    }
    // ZIP validation
    if (!formCard.zip) {
      errors.zip = 'Billing ZIP/postal code is required';
      isValid = false;
    }
    setCardErrors(errors);
    return isValid;
  };
  const handleSaveCard = () => {
    if (!validateCardForm()) return;
    setIsProcessing(true);
    // Simulate API call
    setTimeout(() => {
      const cardNumber = formCard.cardNumber.replace(/\s/g, '');
      const last4 = cardNumber.slice(-4);
      if (cardModalMode === 'edit' && selectedCardId) {
        // Update existing card
        setPaymentMethods(paymentMethods.map(method => method.id === selectedCardId ? {
          ...method,
          name: formCard.name,
          cardNumber,
          last4,
          expiryMonth: formCard.expiryMonth,
          expiryYear: formCard.expiryYear,
          cvc: formCard.cvc,
          zip: formCard.zip
        } : method));
        showToast.success('Payment method updated.');
      } else {
        // Add new card
        const newCard: PaymentMethod = {
          id: Date.now().toString(),
          type: 'card',
          name: formCard.name,
          cardNumber,
          last4,
          expiryMonth: formCard.expiryMonth,
          expiryYear: formCard.expiryYear,
          cvc: formCard.cvc,
          zip: formCard.zip,
          isDefault: paymentMethods.length === 0,
          brand: 'visa' // Simplified for demo
        };
        setPaymentMethods([...paymentMethods, newCard]);
        showToast.success('Payment method added.');
      }
      setIsCardModalOpen(false);
      setIsProcessing(false);
    }, 600);
  };
  // Handle payout modal
  const handleOpenPayoutModal = () => {
    setFormPayout({
      accountHolderName: bankAccount.accountHolderName,
      routingNumber: bankAccount.routingNumber,
      accountNumber: bankAccount.accountNumber,
      accountType: bankAccount.accountType
    });
    setIsPayoutModalOpen(true);
  };
  // Validate payout form
  const validatePayoutForm = (): boolean => {
    const errors = {
      accountHolderName: '',
      routingNumber: '',
      accountNumber: '',
      accountType: ''
    };
    let isValid = true;
    // Account holder name validation
    if (!formPayout.accountHolderName.trim()) {
      errors.accountHolderName = 'Account holder name is required';
      isValid = false;
    }
    // Routing number validation (US: 9 digits)
    if (!formPayout.routingNumber || !/^\d{9}$/.test(formPayout.routingNumber)) {
      errors.routingNumber = 'Enter a valid 9-digit routing number';
      isValid = false;
    }
    // Account number validation
    if (!formPayout.accountNumber || !/^\d{8,17}$/.test(formPayout.accountNumber)) {
      errors.accountNumber = 'Enter a valid account number';
      isValid = false;
    }
    // Account type validation
    if (!formPayout.accountType) {
      errors.accountType = 'Select an account type';
      isValid = false;
    }
    setPayoutErrors(errors);
    return isValid;
  };
  const handleSavePayout = () => {
    if (!validatePayoutForm()) return;
    // If there's an existing account, show confirmation
    if (bankAccount.accountNumber) {
      setIsConfirmPayoutOpen(true);
      return;
    }
    // Otherwise proceed with save
    savePayoutAccount();
  };
  const savePayoutAccount = () => {
    setIsProcessing(true);
    // Simulate API call
    setTimeout(() => {
      const last4 = formPayout.accountNumber.slice(-4);
      setBankAccount({
        bankName: 'Updated Bank',
        accountNumber: formPayout.accountNumber,
        last4,
        accountHolderName: formPayout.accountHolderName,
        routingNumber: formPayout.routingNumber,
        accountType: formPayout.accountType
      });
      setIsPayoutModalOpen(false);
      setIsConfirmPayoutOpen(false);
      setIsProcessing(false);
      showToast.success('Payout account updated.');
    }, 600);
  };
  // Format card number with spaces
  const formatCardNumber = (value: string): string => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(' ');
    } else {
      return value;
    }
  };
  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCardNumber(e.target.value);
    setFormCard({
      ...formCard,
      cardNumber: formatted
    });
  };
  // Add useEffect hook for Esc key handling
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Close non-destructive modals on Escape key
        if (isCardModalOpen) {
          setIsCardModalOpen(false);
        }
        if (isRemoveCardModalOpen) {
          setIsRemoveCardModalOpen(false);
        }
        if (isPayoutModalOpen) {
          setIsPayoutModalOpen(false);
        }
        if (isManageSubOpen) {
          setIsManageSubOpen(false);
        }
        // Note: We don't close destructive modals (isConfirmCancelOpen, isConfirmPayoutOpen)
      }
    };
    // Add event listener when any modal is open
    if (isCardModalOpen || isRemoveCardModalOpen || isPayoutModalOpen || isManageSubOpen) {
      document.addEventListener('keydown', handleEscKey);
    }
    // Cleanup function to remove event listener
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isCardModalOpen, isRemoveCardModalOpen, isPayoutModalOpen, isManageSubOpen]);
  return <main className="pb-6" data-id="element-2815">
      <div className="max-w-7xl mx-auto px-4" data-id="element-2816">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6" data-id="element-2817">
          <div className="mb-4 md:mb-0" data-id="element-2818">
            <h1 className="text-2xl sm:text-3xl font-bold" data-id="element-2819">
              Billing & Payouts
            </h1>
          </div>
          {/* Manage Subscription Button - Changed to green outline */}
          <button onClick={handleOpenManageSub} className={`px-4 py-2 rounded-lg flex items-center justify-center border-2 ${darkMode ? 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10' : 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10'} transition-colors`} data-id="element-2820">
            Manage Subscription
          </button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2821">
          {/* Payment Methods */}
          <div className={`${darkMode ? 'bg-navy-800/80' : 'bg-white'} rounded-xl shadow-sm p-6 lg:col-span-2`} data-id="element-2822">
            <div className="flex items-center mb-4" data-id="element-2823">
              {/* Section icon - Changed to neutral slate */}
              <CreditCardIcon className={`h-5 w-5 mr-2 ${darkMode ? 'text-slate-400' : 'text-slate-600'}`} aria-hidden="true" data-id="element-2824" />
              <h2 className="text-xl font-serif font-semibold" data-id="element-2825">
                Payment Methods
              </h2>
            </div>
            <div className="space-y-4" data-id="element-2826">
              {paymentMethods.map(method => <div key={method.id} className={`p-4 rounded-lg ${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'}`} data-id="element-2827">
                  <div className="flex justify-between items-center" data-id="element-2828">
                    <div className="flex items-center" data-id="element-2829">
                      <div className={`p-2 rounded-md ${darkMode ? 'bg-navy-700' : 'bg-gray-200'} mr-3`} data-id="element-2830">
                        <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-2831">
                          <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="2" data-id="element-2832" />
                          <path d="M3 10H21" stroke="currentColor" strokeWidth="2" data-id="element-2833" />
                          <path d="M7 15H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" data-id="element-2834" />
                          <path d="M12 15H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" data-id="element-2835" />
                        </svg>
                      </div>
                      <div data-id="element-2836">
                        <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2837">
                          {method.brand.charAt(0).toUpperCase() + method.brand.slice(1)}{' '}
                          ending in {method.last4}
                        </p>
                        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-2838">
                          Expires {method.expiryMonth}/{method.expiryYear}
                        </p>
                        {method.id === paymentMethods[0].id && <button onClick={handleOpenManageSub} className={`text-sm mt-1 ${darkMode ? 'text-[#10B981] hover:text-[#059669]' : 'text-[#10B981] hover:text-[#059669]'} hover:underline`} data-id="element-2839">
                            Manage subscription
                          </button>}
                      </div>
                    </div>
                    <div className="flex space-x-2" data-id="element-2840">
                      <button onClick={() => handleOpenCardModal('edit', method.id)} className={`px-3 py-1 rounded text-sm ${darkMode ? 'bg-navy-700 hover:bg-navy-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`} data-id="element-2841">
                        Edit
                      </button>
                      <button onClick={() => handleOpenRemoveCardModal(method.id)} className={`px-3 py-1 rounded text-sm ${darkMode ? 'bg-red-900/30 hover:bg-red-900/50 text-red-300' : 'bg-red-50 hover:bg-red-100 text-red-700'}`} data-id="element-2842">
                        Remove
                      </button>
                    </div>
                  </div>
                </div>)}
              <button onClick={() => handleOpenCardModal('add')} className={`flex items-center px-4 py-3 rounded-lg ${darkMode ? 'text-[#10B981] hover:bg-[#10B981]/10' : 'text-[#10B981] hover:bg-[#10B981]/10'} hover:underline`} data-id="element-2843">
                <PlusIcon className="h-5 w-5 mr-2" data-id="element-2844" />
                Add Payment Method
              </button>
            </div>
          </div>
          {/* Payout Settings */}
          <div className={`${darkMode ? 'bg-navy-800/80' : 'bg-white'} rounded-xl shadow-sm p-6 lg:col-span-1`} data-id="element-2845">
            <div className="flex items-center mb-4" data-id="element-2846">
              {/* Section icon - Changed to neutral slate */}
              <WalletIcon className={`h-5 w-5 mr-2 ${darkMode ? 'text-slate-400' : 'text-slate-600'}`} aria-hidden="true" data-id="element-2847" />
              <h2 className="text-xl font-serif font-semibold" data-id="element-2848">
                Payout Settings
              </h2>
            </div>
            <div className="space-y-4" data-id="element-2849">
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'}`} data-id="element-2850">
                <p className={`font-medium mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2851">
                  Bank Account
                </p>
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-2852">
                  {bankAccount.bankName} ending in {bankAccount.last4}
                </p>
                <button onClick={handleOpenPayoutModal} className={`mt-3 text-sm ${darkMode ? 'text-[#10B981] hover:text-[#059669]' : 'text-[#10B981] hover:text-[#059669]'} hover:underline`} data-id="element-2853">
                  Change account
                </button>
              </div>
              <div data-id="element-2854">
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2855">
                  Payout Schedule
                </label>
                <select className={`w-full px-4 py-2 rounded-lg ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white focus:ring-deep-pink focus:border-deep-pink' : 'bg-white border-gray-300 text-gray-900 focus:ring-deep-pink focus:border-deep-pink'} border`} defaultValue="monthly" data-id="element-2856">
                  <option value="weekly" data-id="element-2857">Weekly</option>
                  <option value="biweekly" data-id="element-2858">Bi-weekly</option>
                  <option value="monthly" data-id="element-2859">Monthly</option>
                </select>
              </div>
            </div>
          </div>
          {/* Transaction History */}
          <div className={`${darkMode ? 'bg-navy-800/80' : 'bg-white'} rounded-xl shadow-sm p-6 lg:col-span-3`} data-id="element-2860">
            <div className="flex items-center mb-4" data-id="element-2861">
              {/* Section icon - Changed to neutral slate */}
              <FileTextIcon className={`h-5 w-5 mr-2 ${darkMode ? 'text-slate-400' : 'text-slate-600'}`} aria-hidden="true" data-id="element-2862" />
              <h2 className="text-xl font-serif font-semibold" data-id="element-2863">
                Transaction History
              </h2>
            </div>
            <div className={`overflow-hidden rounded-lg ${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'}`} data-id="element-2864">
              <div className="overflow-x-auto" data-id="element-2865">
                <table className="min-w-full divide-y divide-gray-700" data-id="element-2866">
                  <thead className={darkMode ? 'bg-navy-900/80' : 'bg-gray-100/80'} data-id="element-2867">
                    <tr data-id="element-2868">
                      <th scope="col" className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`} data-id="element-2869">
                        Date
                      </th>
                      <th scope="col" className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`} data-id="element-2870">
                        Description
                      </th>
                      <th scope="col" className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`} data-id="element-2871">
                        Amount
                      </th>
                      <th scope="col" className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`} data-id="element-2872">
                        Status
                      </th>
                      <th scope="col" className={`px-6 py-3 text-right text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`} data-id="element-2873">
                        Receipt
                      </th>
                    </tr>
                  </thead>
                  <tbody className={darkMode ? 'bg-navy-900/50 divide-y divide-gray-700' : 'bg-white divide-y divide-gray-200'} data-id="element-2874">
                    <tr data-id="element-2875">
                      <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-2876">
                        May 15, 2023
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2877">
                        Monthly Subscription
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2878">
                        $29.99
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm" data-id="element-2879">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${darkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800'}`} data-id="element-2880">
                          Paid
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right" data-id="element-2881">
                        {/* Download icon - Changed to neutral gray with green hover */}
                        <button className={`inline-flex items-center ${darkMode ? 'text-gray-400 hover:text-[#10B981]' : 'text-gray-600 hover:text-[#10B981]'} transition-colors`} data-id="element-2882">
                          <DownloadIcon className="h-4 w-4 mr-1" data-id="element-2883" />
                          PDF
                        </button>
                      </td>
                    </tr>
                    <tr data-id="element-2884">
                      <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-2885">
                        Apr 15, 2023
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2886">
                        Monthly Subscription
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2887">
                        $29.99
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm" data-id="element-2888">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${darkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800'}`} data-id="element-2889">
                          Paid
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right" data-id="element-2890">
                        {/* Download icon - Changed to neutral gray with green hover */}
                        <button className={`inline-flex items-center ${darkMode ? 'text-gray-400 hover:text-[#10B981]' : 'text-gray-600 hover:text-[#10B981]'} transition-colors`} data-id="element-2891">
                          <DownloadIcon className="h-4 w-4 mr-1" data-id="element-2892" />
                          PDF
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        {/* Footer removed - provided by TeacherLayout */}

        {/* Manage Subscription Modal */}
        {isManageSubOpen && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={e => {
        // Close modal when clicking outside (on the overlay)
        if (e.target === e.currentTarget) {
          setIsManageSubOpen(false);
        }
      }} data-id="element-2893">
            <div className={`${darkMode ? 'bg-navy-800' : 'bg-white'} rounded-xl p-6 max-w-md w-full mx-4`} data-id="element-2894">
              <div className="flex justify-between items-center mb-4" data-id="element-2895">
                <h3 className={`text-xl font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2896">
                  Manage Subscription
                </h3>
                <button onClick={() => setIsManageSubOpen(false)} className={`p-1 rounded-full ${darkMode ? 'hover:bg-navy-700' : 'hover:bg-gray-200'}`} data-id="element-2897">
                  <XIcon className={`h-6 w-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2898" />
                </button>
              </div>
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-navy-700' : 'bg-gray-50'} mb-6`} data-id="element-2899">
                <div className="flex items-center justify-between mb-2" data-id="element-2900">
                  <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2901">
                    Current Plan
                  </h4>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${subscription.isActive ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'}`} data-id="element-2902">
                    {subscription.isActive ? 'Active' : 'Canceled'}
                  </span>
                </div>
                <div className="flex items-baseline mb-1" data-id="element-2903">
                  <span className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2904">
                    {subscription.plan} ({subscription.interval})
                  </span>
                </div>
                <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2905">
                  ${subscription.price.toFixed(2)} / month
                </p>
                <div className="flex items-center mt-4" data-id="element-2906">
                  <CalendarIcon className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'} mr-2`} data-id="element-2907" />
                  <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-2908">
                    {subscription.isActive ? `Next renewal: ${subscription.nextRenewal}` : `Access until: ${subscription.nextRenewal}`}
                  </p>
                </div>
              </div>
              <div className="flex justify-end" data-id="element-2909">
                {subscription.isActive ? <button onClick={handleOpenConfirmCancel} className={`px-4 py-2 ${darkMode ? 'bg-red-900/30 hover:bg-red-900/50 text-red-300' : 'bg-red-50 hover:bg-red-100 text-red-700'} rounded-lg transition-colors`} data-id="element-2910">
                    Cancel subscription
                  </button> : <button onClick={() => setIsManageSubOpen(false)} className={`px-4 py-2 ${darkMode ? 'bg-navy-700 hover:bg-navy-600' : 'bg-gray-100 hover:bg-gray-200'} rounded-lg transition-colors`} data-id="element-2911">
                    Close
                  </button>}
              </div>
            </div>
          </div>}

        {/* Confirm Cancellation Modal - No outside click close (destructive) */}
        {isConfirmCancelOpen && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-id="element-2912">
            <div className={`${darkMode ? 'bg-navy-800' : 'bg-white'} rounded-xl p-6 max-w-md w-full mx-4`} data-id="element-2913">
              <div className="flex items-center mb-4" data-id="element-2914">
                <AlertCircleIcon className="h-6 w-6 text-red-500 mr-2" data-id="element-2915" />
                <h3 className={`text-xl font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2916">
                  Cancel subscription?
                </h3>
              </div>
              <p className={`mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-2917">
                You'll lose premium features at the end of your current billing
                period. You can re-subscribe anytime.
              </p>
              <div className="flex justify-end space-x-3" data-id="element-2918">
                <button onClick={() => setIsConfirmCancelOpen(false)} className={`px-4 py-2 ${darkMode ? 'border border-gray-500 text-gray-300 hover:bg-navy-700' : 'border border-gray-300 text-gray-700 hover:bg-gray-100'} rounded-lg transition-colors`} data-id="element-2919">
                  Back
                </button>
                <button onClick={handleConfirmCancel} disabled={isProcessing} className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors" data-id="element-2920">
                  {isProcessing ? 'Processing...' : 'Confirm Cancel'}
                </button>
              </div>
            </div>
          </div>}

        {/* Card Modal (Edit/Add) */}
        {isCardModalOpen && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={e => {
        // Close modal when clicking outside (on the overlay)
        if (e.target === e.currentTarget) {
          setIsCardModalOpen(false);
        }
      }} data-id="element-2921">
            <div className={`${darkMode ? 'bg-navy-800' : 'bg-white'} rounded-xl p-6 max-w-md w-full mx-4`} data-id="element-2922">
              <div className="flex justify-between items-center mb-4" data-id="element-2923">
                <h3 className={`text-xl font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2924">
                  {cardModalMode === 'add' ? 'Add Payment Method' : 'Edit Payment Method'}
                </h3>
                <button onClick={() => setIsCardModalOpen(false)} className={`p-1 rounded-full ${darkMode ? 'hover:bg-navy-700' : 'hover:bg-gray-200'}`} data-id="element-2925">
                  <XIcon className={`h-6 w-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2926" />
                </button>
              </div>
              <div className="space-y-4" data-id="element-2927">
                {/* Name on card */}
                <div data-id="element-2928">
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2929">
                    Name on card
                  </label>
                  <input type="text" value={formCard.name} onChange={e => setFormCard({
                ...formCard,
                name: e.target.value
              })} className={`w-full px-3 py-2 rounded-lg ${darkMode ? 'bg-navy-900/80 border-navy-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'} border focus:ring-2 focus:ring-deep-pink`} placeholder="John Smith" data-id="element-2930" />
                  {cardErrors.name && <p className="mt-1 text-sm text-red-500" data-id="element-2931">
                      {cardErrors.name}
                    </p>}
                </div>
                {/* Card number */}
                <div data-id="element-2932">
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2933">
                    Card number
                  </label>
                  <div className="relative" data-id="element-2934">
                    <input type="text" value={formCard.cardNumber} onChange={handleCardNumberChange} className={`w-full px-3 py-2 rounded-lg ${darkMode ? 'bg-navy-900/80 border-navy-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'} border focus:ring-2 focus:ring-deep-pink`} placeholder="4242 4242 4242 4242" maxLength={19} data-id="element-2935" />
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2" data-id="element-2936">
                      <CreditCardIcon className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2937" />
                    </div>
                  </div>
                  {cardErrors.cardNumber && <p className="mt-1 text-sm text-red-500" data-id="element-2938">
                      {cardErrors.cardNumber}
                    </p>}
                </div>
                {/* Expiry and CVC */}
                <div className="grid grid-cols-2 gap-4" data-id="element-2939">
                  <div data-id="element-2940">
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2941">
                      Expiry date
                    </label>
                    <div className="flex space-x-2" data-id="element-2942">
                      <select value={formCard.expiryMonth} onChange={e => setFormCard({
                    ...formCard,
                    expiryMonth: e.target.value
                  })} className={`w-1/2 px-3 py-2 rounded-lg ${darkMode ? 'bg-navy-900/80 border-navy-600 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:ring-2 focus:ring-deep-pink`} data-id="element-2943">
                        <option value="" data-id="element-2944">MM</option>
                        {Array.from({
                      length: 12
                    }, (_, i) => {
                      const month = i + 1;
                      return <option key={month} value={month.toString().padStart(2, '0')} data-id="element-2945">
                                {month.toString().padStart(2, '0')}
                              </option>;
                    })}
                      </select>
                      <select value={formCard.expiryYear} onChange={e => setFormCard({
                    ...formCard,
                    expiryYear: e.target.value
                  })} className={`w-1/2 px-3 py-2 rounded-lg ${darkMode ? 'bg-navy-900/80 border-navy-600 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:ring-2 focus:ring-deep-pink`} data-id="element-2946">
                        <option value="" data-id="element-2947">YY</option>
                        {Array.from({
                      length: 10
                    }, (_, i) => {
                      const year = new Date().getFullYear() + i;
                      return <option key={year} value={year.toString().slice(-2)} data-id="element-2948">
                                {year.toString().slice(-2)}
                              </option>;
                    })}
                      </select>
                    </div>
                    {cardErrors.expiry && <p className="mt-1 text-sm text-red-500" data-id="element-2949">
                        {cardErrors.expiry}
                      </p>}
                  </div>
                  <div data-id="element-2950">
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2951">
                      CVC
                    </label>
                    <input type="text" value={formCard.cvc} onChange={e => setFormCard({
                  ...formCard,
                  cvc: e.target.value
                })} className={`w-full px-3 py-2 rounded-lg ${darkMode ? 'bg-navy-900/80 border-navy-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'} border focus:ring-2 focus:ring-deep-pink`} placeholder="123" maxLength={4} data-id="element-2952" />
                    {cardErrors.cvc && <p className="mt-1 text-sm text-red-500" data-id="element-2953">
                        {cardErrors.cvc}
                      </p>}
                  </div>
                </div>
                {/* Billing ZIP */}
                <div data-id="element-2954">
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2955">
                    Billing ZIP/Postal code
                  </label>
                  <input type="text" value={formCard.zip} onChange={e => setFormCard({
                ...formCard,
                zip: e.target.value
              })} className={`w-full px-3 py-2 rounded-lg ${darkMode ? 'bg-navy-900/80 border-navy-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'} border focus:ring-2 focus:ring-deep-pink`} placeholder="10001" data-id="element-2956" />
                  {cardErrors.zip && <p className="mt-1 text-sm text-red-500" data-id="element-2957">
                      {cardErrors.zip}
                    </p>}
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3" data-id="element-2958">
                <button onClick={() => setIsCardModalOpen(false)} className={`px-4 py-2 ${darkMode ? 'border border-gray-500 text-gray-300 hover:bg-navy-700' : 'border border-gray-300 text-gray-700 hover:bg-gray-100'} rounded-lg transition-colors`} data-id="element-2959">
                  Cancel
                </button>
                <button onClick={handleSaveCard} disabled={isProcessing} className={`px-4 py-2 border-2 ${darkMode ? 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10' : 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10'} disabled:border-gray-600 disabled:text-gray-600 disabled:cursor-not-allowed rounded-lg transition-colors`} data-id="element-2960">
                  {isProcessing ? 'Processing...' : cardModalMode === 'add' ? 'Add Card' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>}

        {/* Remove Card Confirmation Modal */}
        {isRemoveCardModalOpen && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={e => {
        // Close modal when clicking outside (on the overlay)
        if (e.target === e.currentTarget) {
          setIsRemoveCardModalOpen(false);
        }
      }} data-id="element-2961">
            <div className={`${darkMode ? 'bg-navy-800' : 'bg-white'} rounded-xl p-6 max-w-md w-full mx-4`} data-id="element-2962">
              <div className="flex items-center mb-4" data-id="element-2963">
                <AlertCircleIcon className="h-6 w-6 text-red-500 mr-2" data-id="element-2964" />
                <h3 className={`text-xl font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2965">
                  Remove payment method?
                </h3>
              </div>
              <p className={`mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-2966">
                Are you sure you want to remove this payment method? You can add
                it again later.
              </p>
              <div className="flex justify-end space-x-3" data-id="element-2967">
                <button onClick={() => setIsRemoveCardModalOpen(false)} className={`px-4 py-2 ${darkMode ? 'border border-gray-500 text-gray-300 hover:bg-navy-700' : 'border border-gray-300 text-gray-700 hover:bg-gray-100'} rounded-lg transition-colors`} data-id="element-2968">
                  Keep
                </button>
                <button onClick={handleRemoveCard} disabled={isProcessing} className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors" data-id="element-2969">
                  {isProcessing ? 'Processing...' : 'Remove'}
                </button>
              </div>
            </div>
          </div>}

        {/* Payout Account Modal */}
        {isPayoutModalOpen && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={e => {
        // Close modal when clicking outside (on the overlay)
        if (e.target === e.currentTarget) {
          setIsPayoutModalOpen(false);
        }
      }} data-id="element-2970">
            <div className={`${darkMode ? 'bg-navy-800' : 'bg-white'} rounded-xl p-6 max-w-md w-full mx-4`} data-id="element-2971">
              <div className="flex justify-between items-center mb-4" data-id="element-2972">
                <h3 className={`text-xl font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2973">
                  Change payout account
                </h3>
                <button onClick={() => setIsPayoutModalOpen(false)} className={`p-1 rounded-full ${darkMode ? 'hover:bg-navy-700' : 'hover:bg-gray-200'}`} data-id="element-2974">
                  <XIcon className={`h-6 w-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2975" />
                </button>
              </div>
              <div className="space-y-4" data-id="element-2976">
                {/* Account holder name */}
                <div data-id="element-2977">
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2978">
                    Account holder name
                  </label>
                  <input type="text" value={formPayout.accountHolderName} onChange={e => setFormPayout({
                ...formPayout,
                accountHolderName: e.target.value
              })} className={`w-full px-3 py-2 rounded-lg ${darkMode ? 'bg-navy-900/80 border-navy-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'} border focus:ring-2 focus:ring-deep-pink`} placeholder="John Smith" data-id="element-2979" />
                  {payoutErrors.accountHolderName && <p className="mt-1 text-sm text-red-500" data-id="element-2980">
                      {payoutErrors.accountHolderName}
                    </p>}
                </div>
                {/* Routing number */}
                <div data-id="element-2981">
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2982">
                    Routing number
                  </label>
                  <input type="text" value={formPayout.routingNumber} onChange={e => setFormPayout({
                ...formPayout,
                routingNumber: e.target.value.replace(/\D/g, '')
              })} className={`w-full px-3 py-2 rounded-lg ${darkMode ? 'bg-navy-900/80 border-navy-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'} border focus:ring-2 focus:ring-deep-pink`} placeholder="123456789" maxLength={9} data-id="element-2983" />
                  {payoutErrors.routingNumber && <p className="mt-1 text-sm text-red-500" data-id="element-2984">
                      {payoutErrors.routingNumber}
                    </p>}
                </div>
                {/* Account number */}
                <div data-id="element-2985">
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2986">
                    Account number
                  </label>
                  <input type="text" value={formPayout.accountNumber} onChange={e => setFormPayout({
                ...formPayout,
                accountNumber: e.target.value.replace(/\D/g, '')
              })} className={`w-full px-3 py-2 rounded-lg ${darkMode ? 'bg-navy-900/80 border-navy-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'} border focus:ring-2 focus:ring-deep-pink`} placeholder="12345678901234" data-id="element-2987" />
                  {payoutErrors.accountNumber && <p className="mt-1 text-sm text-red-500" data-id="element-2988">
                      {payoutErrors.accountNumber}
                    </p>}
                </div>
                {/* Account type */}
                <div data-id="element-2989">
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2990">
                    Account type
                  </label>
                  <select value={formPayout.accountType} onChange={e => setFormPayout({
                ...formPayout,
                accountType: e.target.value
              })} className={`w-full px-3 py-2 rounded-lg ${darkMode ? 'bg-navy-900/80 border-navy-600 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:ring-2 focus:ring-deep-pink`} data-id="element-2991">
                    <option value="checking" data-id="element-2992">Checking</option>
                    <option value="savings" data-id="element-2993">Savings</option>
                  </select>
                  {payoutErrors.accountType && <p className="mt-1 text-sm text-red-500" data-id="element-2994">
                      {payoutErrors.accountType}
                    </p>}
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3" data-id="element-2995">
                <button onClick={() => setIsPayoutModalOpen(false)} className={`px-4 py-2 ${darkMode ? 'border border-gray-500 text-gray-300 hover:bg-navy-700' : 'border border-gray-300 text-gray-700 hover:bg-gray-100'} rounded-lg transition-colors`} data-id="element-2996">
                  Cancel
                </button>
                <button onClick={handleSavePayout} disabled={isProcessing} className={`px-4 py-2 ${darkMode ? 'bg-deep-pink hover:bg-burgundy' : 'bg-deep-pink hover:bg-burgundy'} disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors`} data-id="element-2997">
                  {isProcessing ? 'Processing...' : 'Save'}
                </button>
              </div>
            </div>
          </div>}

        {/* Confirm Payout Change Modal - No outside click close (destructive) */}
        {isConfirmPayoutOpen && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-id="element-2998">
            <div className={`${darkMode ? 'bg-navy-800' : 'bg-white'} rounded-xl p-6 max-w-md w-full mx-4`} data-id="element-2999">
              <div className="flex items-center mb-4" data-id="element-3000">
                <AlertCircleIcon className="h-6 w-6 text-amber-500 mr-2" data-id="element-3001" />
                <h3 className={`text-xl font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-3002">
                  Replace current payout account?
                </h3>
              </div>
              <p className={`mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-3003">
                This will replace your existing payout account information.
                Future payouts will be sent to the new account.
              </p>
              <div className="flex justify-end space-x-3" data-id="element-3004">
                <button onClick={() => setIsConfirmPayoutOpen(false)} className={`px-4 py-2 ${darkMode ? 'border border-gray-500 text-gray-300 hover:bg-navy-700' : 'border border-gray-300 text-gray-700 hover:bg-gray-100'} rounded-lg transition-colors`} data-id="element-3005">
                  Back
                </button>
                <button onClick={savePayoutAccount} disabled={isProcessing} className={`px-4 py-2 ${darkMode ? 'bg-deep-pink hover:bg-burgundy' : 'bg-deep-pink hover:bg-burgundy'} disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors`} data-id="element-3006">
                  {isProcessing ? 'Processing...' : 'Confirm Replace'}
                </button>
              </div>
            </div>
          </div>}
      </div>
    </main>;
};